import { Link } from "react-router";

import { PUBLIC_ROUTES } from "@/constants/routes";

/**
 * docs/ui/design-system.md's Footer component (`variant: full`), the
 * multi-column marketing footer used on Home. Only two links are real:
 * `All Categories` resolves to `/search` (routes.md's "category browsing"
 * naming decision — there's no dedicated `/categories` page) and
 * `Help Center` is a registered route (`/help`), even though its page is
 * still a placeholder. The rest of the wireframe's link set (About/
 * Careers/Press, Terms/Privacy/Cookies) has no route decided in
 * docs/ui/routes.md yet, so those render as plain static text instead of
 * dead "#" anchors or fabricated routes.
 *
 * The wireframe's "Download our app" column is dropped entirely, not just
 * de-linked: this product has no iOS/Android app, and App Store/Google Play
 * buttons would claim otherwise regardless of whether they're clickable.
 *
 * `variant: legal-only` (auth screens, per design-system.md) isn't built
 * here — this component only covers the Home/marketing use case requested
 * so far.
 *
 * Colors are fixed (`slate-900`/`white`), not the theme-relative
 * `bg-foreground`/`text-background` tokens this originally used — those
 * flip polarity between light and dark mode (`--foreground` is near-black
 * in light mode, near-white in dark mode), so in dark mode the footer was
 * inverting to a *light* background with dark text: a stray white block
 * sitting in an otherwise dark page. A marketing footer like this is
 * supposed to read as a fixed dark section regardless of theme — the same
 * reasoning `HomePage.tsx`'s hero banner comment gives for its own fixed
 * `primary-600`/`primary-900` gradient instead of theme-relative colors.
 */
export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-slate-900 text-white">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 sm:grid-cols-3">
        <div>
          <span className="text-h3 font-bold">BazaarX</span>
          <p className="mt-4 text-body-sm text-white/60">
            Buy and sell pre-owned goods with verified listings and secure transactions.
          </p>
        </div>

        <div>
          <h4 className="text-h4">Marketplace</h4>
          <ul className="mt-4 space-y-3 text-body-sm text-white/60">
            <li>
              <Link to={PUBLIC_ROUTES.search} className="transition-colors hover:text-white">
                All Categories
              </Link>
            </li>
            <li>
              <Link to={PUBLIC_ROUTES.helpCenter} className="transition-colors hover:text-white">
                Help Center
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-h4">Company</h4>
          <ul className="mt-4 space-y-3 text-body-sm text-white/40">
            <li>About BazaarX</li>
            <li>Careers</li>
            <li>Press Room</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 px-4 py-6 text-caption text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} BazaarX. All rights reserved.</p>
          <div className="flex gap-6">
            <span>Terms of Service</span>
            <span>Privacy Policy</span>
            <span>Cookies</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
